import React from "react";
import { createPortal } from "react-dom";
import { ChevronDown, FileSearch, FileText, Import, ListTree } from "lucide-react";
import { useEscapeClose } from "../../../hooks/useEscapeClose";
import { UI_Z_INDEX } from "../../../utils/uiZIndex";
import { GaebActionButton } from "./GaebActionButton";
import { UglActionButton } from "./UglActionButton";
import { LvPdfImportDialog } from "./LvPdfImportDialog";

type OfferImportActionButtonProps = {
  offeringId: string;
  onImported?: () => void | Promise<void>;
  className?: string;
};

export const OfferImportActionButton: React.FC<OfferImportActionButtonProps> = ({
  offeringId,
  onImported,
  className,
}) => {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [dialog, setDialog] = React.useState<"gaeb" | "ugl" | "pdf" | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [panelPos, setPanelPos] = React.useState<{ top: number; left: number; width: number } | null>(null);

  const closeMenu = React.useCallback(() => setMenuOpen(false), []);
  useEscapeClose(closeMenu, menuOpen && dialog == null);

  const updatePanelPos = React.useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const width = Math.max(rect.width, 240);
    let left = rect.left;
    if (left + width > window.innerWidth - 8) left = Math.max(8, window.innerWidth - width - 8);
    setPanelPos({ top: rect.bottom + 4, left, width });
  }, []);

  React.useEffect(() => {
    if (!menuOpen) return;
    updatePanelPos();
    const onReposition = () => updatePanelPos();
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      closeMenu();
    };
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [menuOpen, closeMenu, updatePanelPos]);

  const pick = (next: "gaeb" | "ugl" | "pdf") => {
    closeMenu();
    setDialog(next);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={className}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((open) => !open)}
      >
        <Import className="w-3.5 h-3.5 shrink-0 text-pipedrive-blue" />
        Importieren
        <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-70" />
      </button>

      {menuOpen && panelPos
        ? createPortal(
            <div
              ref={panelRef}
              role="menu"
              className="fixed rounded-md border border-app-border bg-app-surface py-1 shadow-lg"
              style={{
                top: panelPos.top,
                left: panelPos.left,
                width: panelPos.width,
                zIndex: UI_Z_INDEX.portalPanel,
              }}
            >
              <button
                type="button"
                role="menuitem"
                className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-app-hover"
                onClick={() => pick("gaeb")}
              >
                <FileText className="mt-0.5 h-4 w-4 shrink-0 text-pipedrive-blue" />
                <span>
                  <span className="block text-sm font-semibold text-app-text">GAEB-Datei</span>
                  <span className="block text-xs text-app-text-subtle">Leistungsverzeichnis ins Angebot übernehmen</span>
                </span>
              </button>
              <button
                type="button"
                role="menuitem"
                className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-app-hover"
                onClick={() => pick("pdf")}
              >
                <FileSearch className="mt-0.5 h-4 w-4 shrink-0 text-pipedrive-blue" />
                <span>
                  <span className="block text-sm font-semibold text-app-text">Leistungsverzeichnis aus PDF</span>
                  <span className="block text-xs text-app-text-subtle">Positionen automatisch aus dem PDF übernehmen</span>
                </span>
              </button>
              <button
                type="button"
                role="menuitem"
                className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-app-hover"
                onClick={() => pick("ugl")}
              >
                <ListTree className="mt-0.5 h-4 w-4 shrink-0 text-pipedrive-blue" />
                <span>
                  <span className="block text-sm font-semibold text-app-text">UGL-Stückliste</span>
                  <span className="block text-xs text-app-text-subtle">Stückliste an vorhandene Positionen hängen</span>
                </span>
              </button>
            </div>,
            document.body,
          )
        : null}

      <GaebActionButton
        offeringId={offeringId}
        onImported={onImported}
        importOnly
        hideTrigger
        open={dialog === "gaeb"}
        onOpenChange={(next) => {
          if (!next) setDialog(null);
        }}
      />
      <UglActionButton
        offeringId={offeringId}
        onImported={onImported}
        hideTrigger
        open={dialog === "ugl"}
        onOpenChange={(next) => {
          if (!next) setDialog(null);
        }}
      />
      <LvPdfImportDialog
        offeringId={offeringId}
        onImported={onImported}
        open={dialog === "pdf"}
        onClose={() => setDialog(null)}
        onSwitchToGaeb={() => setDialog("gaeb")}
      />
    </>
  );
};
